import { Link } from "@/lib/router-compat";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, FileText, CheckCircle, Upload, Sparkles, ArrowRight } from "lucide-react";

import { ArticleTOC } from "@/components/resources/ArticleTOC";
import { ArticleJsonLd } from "@/components/resources/ArticleJsonLd";


const sections = [
  { id: "why", title: "Why Turn PDFs into Revision Sheets?" },
  { id: "step-by-step", title: "Step-by-Step Tutorial" },
  { id: "tips", title: "Tips for Better Results" },
  { id: "after", title: "What to Do After Generating" },
  { id: "formats", title: "Beyond PDFs: Other Formats" },
  { id: "key-takeaways", title: "Key Takeaways" },
];

export default function PDFToSheetGuide() {
  useSEO({
    title: "How to Turn a Course PDF into a Revision Sheet: Tutorial | System Atlas",
    description: "Step-by-step tutorial to transform your course PDFs into structured, study-ready revision sheets using AI. Upload, generate, refine, and study smarter.",
    type: "article",
    alternates: [
      { hreflang: "en", href: "https://systematlas.fr/learn/pdf-to-sheet" },
      { hreflang: "fr", href: "https://systematlas.fr/fr/learn/pdf-to-sheet" },
      { hreflang: "x-default", href: "https://systematlas.fr/learn/pdf-to-sheet" },
    ],
  });

  return (
    <>
      <ArticleJsonLd title="How to Turn a Course PDF into a Revision Sheet: Tutorial" description="Step-by-step tutorial to transform your course PDFs into revision sheets." publishedDate="2026-02-23" modifiedDate="2026-02-23" inLanguage="en" />
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/learn" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm mb-8"><ArrowLeft className="w-4 h-4" />Back to guides</Link>
        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-6 tracking-tight">How to Turn a Course PDF into a Revision Sheet</h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">You have a 40-page PDF from your professor. You need to study it for an exam. Instead of reading it 3 times and hoping for the best, here's how to transform it into a structured revision sheet in minutes.</p>
          <ArticleTOC sections={sections} />

          <section id="why" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><FileText className="w-5 h-5 text-primary" />Why Turn PDFs into Revision Sheets?</h2>
            <ul className="space-y-3">
              {[
                { b: "PDFs are not study tools:", t: "They're designed for reading, not learning. No structure, no self-testing, no hierarchy of importance." },
                { b: "Time savings:", t: "Manually summarizing a 40-page PDF takes 2-3 hours. AI can create a structured first draft in under 2 minutes." },
                { b: "Better retention:", t: "A structured sheet with definitions, examples, and takeaways is far more effective for memory than a raw PDF." },
                { b: "Active engagement:", t: "Reviewing and refining the AI-generated sheet forces you to engage actively with the content." },
              ].map(i => (
                <li key={i.b} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                  <span className="text-muted-foreground text-sm"><strong className="text-foreground">{i.b}</strong> {i.t}</span>
                </li>
              ))}
            </ul>
          </section>

          <section id="step-by-step" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Upload className="w-5 h-5 text-primary" />Step-by-Step Tutorial</h2>
            <div className="space-y-4">
              {[
                { n: "1", t: "Prepare your PDF", d: "Make sure your PDF is text-based (not scanned images). If it's scanned, use an OCR tool first. Ideally, use the original lecture slides or professor's notes." },
                { n: "2", t: "Upload to System Atlas", d: "Go to System Atlas, select 'Files', and drag your PDF into the upload zone. The AI extracts and processes the text automatically." },
                { n: "3", t: "Take the diagnostic test", d: "A 10-question quiz evaluates your current knowledge level. This helps the AI calibrate the sheet to your understanding. beginner, intermediate, or advanced." },
                { n: "4", t: "Review the generated sheet", d: "The AI produces a 7-part structured sheet: Title, Objective, Definition, Breakdown, Explanation, Examples, and Key Takeaways. Read it critically." },
                { n: "5", t: "Refine with AI tools", d: "Use 'Simplify' for complex sections, 'Give examples' for abstract concepts, or ask questions in the chat for specific clarifications." },
                { n: "6", t: "Test yourself", d: "Click 'Test me' to generate MCQ or comprehension questions. This is where real learning happens." },
              ].map(i => (
                <div key={i.n} className="p-4 border border-border/60 rounded-lg">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-medium text-sm">{i.n}</span>
                    <h3 className="font-medium">{i.t}</h3>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed">{i.d}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="tips" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5 text-primary" />Tips for Better Results</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-2 text-sm text-primary">Do</h3>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>✓ Use the original professor's PDF</li>
                  <li>✓ Upload one topic/chapter at a time</li>
                  <li>✓ Take the diagnostic test honestly</li>
                  <li>✓ Read the sheet critically, not passively</li>
                  <li>✓ Use the quiz feature after each sheet</li>
                  <li>✓ Rename sheets with clear, specific titles</li>
                </ul>
              </div>
              <div className="p-4 border border-border/60 rounded-lg">
                <h3 className="font-medium mb-2 text-sm text-destructive">Don't</h3>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>✗ Upload an entire 200-page textbook at once</li>
                  <li>✗ Accept the sheet without reading it</li>
                  <li>✗ Skip the diagnostic. it calibrates quality</li>
                  <li>✗ Ignore formulas or calculations without verifying</li>
                  <li>✗ Treat the sheet as your only study source</li>
                  <li>✗ Forget to come back for spaced reviews</li>
                </ul>
              </div>
            </div>
          </section>

          <section id="after" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">What to Do After Generating</h2>
            <div className="space-y-3">
              {[
                { t: "Immediately: Read and flag", d: "Read the sheet once. Flag anything that seems wrong or unclear. Verify calculations." },
                { t: "Same day: Take a quiz", d: "Use 'Test me' to take a 10-question MCQ. This first test is crucial for identifying gaps." },
                { t: "Day 3: Recall session", d: "Close the sheet. Try to explain the main concepts from memory. Reopen to check what you missed." },
                { t: "Day 7: Full review", d: "Review the sheet again. Take another quiz. Update the sheet if you've learned new nuances." },
                { t: "Before exam: Share and discuss", d: "Share the sheet with classmates using the share button. Discussing differences in understanding deepens learning." },
              ].map(i => (
                <div key={i.t} className="p-4 border-l-4 border-primary bg-primary/5 rounded-r-lg">
                  <h3 className="font-medium mb-1 text-sm">{i.t}</h3>
                  <p className="text-xs text-muted-foreground">{i.d}</p>
                </div>
              ))}
            </div>
          </section>

          <section id="formats" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Beyond PDFs: Other Formats</h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">System Atlas handles more than just PDFs:</p>
            <ul className="space-y-2 text-muted-foreground text-sm">
              <li>• <strong className="text-foreground">Text:</strong> Paste your notes, essays, or copied content directly</li>
              <li>• <strong className="text-foreground">URLs:</strong> Paste a web article link and get a sheet from it</li>
              <li>• <strong className="text-foreground">YouTube:</strong> Paste a video URL and get a sheet from the transcript</li>
              <li>• <strong className="text-foreground">Images:</strong> Upload photos of handwritten notes or whiteboard content</li>
            </ul>
          </section>

          <section id="key-takeaways" className="p-6 bg-primary/5 border border-primary/15 rounded-xl">
            <h2 className="text-lg font-medium mb-3">Key Takeaways</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Upload one topic at a time for best results</li>
              <li>• The diagnostic test calibrates the sheet to your level. don't skip it</li>
              <li>• Always read the generated sheet critically and verify facts</li>
              <li>• Use AI tools (Simplify, Examples, Quiz) to deepen understanding</li>
              <li>• Test yourself on the same day. the first quiz is the most valuable</li>
              <li>• Schedule spaced reviews at 1, 3, 7, and 14 days</li>
              <li>• Share sheets with classmates to create discussion and deeper learning</li>
            </ul>
          </section>
        </article>

        <nav className="mt-12 pt-8 border-t border-border/40">
          <h3 className="font-medium mb-4">Related Guides</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/learn/perfect-revision-sheet" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">The Perfect Revision Sheet</h4>
              <p className="text-xs text-muted-foreground">Structure, formatting, and content strategy</p>
            </Link>
            <Link to="/learn/ai-summarize-lectures" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">Summarize Lectures with AI</h4>
              <p className="text-xs text-muted-foreground">AI-powered note summarization best practices</p>
            </Link>
            <Link to="/learn/active-recall" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors">
              <h4 className="font-medium mb-1 text-sm">Active Recall</h4>
              <p className="text-xs text-muted-foreground">Why testing yourself works</p>
            </Link>
          </div>
        </nav>

        <div className="mt-8 text-center p-8 bg-primary/5 border border-primary/15 rounded-xl">
          <h2 className="text-lg font-medium mb-2">Ready to try it?</h2>
          <p className="text-sm text-muted-foreground mb-6">Upload your first PDF and get a structured revision sheet in under 2 minutes.</p>
          <Link to="/">
            <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors">
              Upload a PDF now <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      </div>
    </>
  );
}
